// TEMPORARY ADMIN TOOL — remove after production stabilization (ver
// auditoría Fase 1.7 — "Reconstrucción Legacy"). Componente aislado a
// propósito: no importa nada de Liquidaciones/Elite ni viceversa — borrar
// este archivo + la línea que lo importa alcanza para eliminarlo por
// completo. Llama exclusivamente POST /v1/utils/reconstruct-legacy, que
// reutiliza reconstructWeek()/runSync() (el mismo camino que ya usa el
// poller en vivo) con el mismo advisory lock — nunca una segunda
// implementación de importación. Reconstruir legacy.envios por UPSERT no
// toca Snapshot/Overrides/Reportes/Liquidaciones (ver el comentario en
// server/legacy/utils.js) — el flujo preferido es
// Deploy → Reconstruir rango → el poller normal continúa, sin reset previo.
import React, { useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { useAuth } from "../../context/AuthContext";
import { authFetch } from "../../lib/authFetch";

type DayResult = {
  date: string;
  received?: number;
  inserted?: number;
  updated?: number;
  unchanged?: number;
  skipped?: number;
  durationMs?: number;
  error?: string;
};

type ReconstructResponse = {
  ok: boolean;
  from?: string;
  to?: string;
  days?: DayResult[];
  totals?: { received: number; inserted: number; updated: number; unchanged: number; skipped: number; failedDays: number };
  error?: string;
};

const sectionStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "12px" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "15px", fontWeight: 700, color: "var(--text-primary)" };
const noteStyle: React.CSSProperties = { margin: 0, fontSize: "12px", color: "var(--text-secondary)" };
const rowStyle: React.CSSProperties = { display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end" };
const fieldStyle: React.CSSProperties = { minWidth: "160px" };
const overlayStyle: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" };
const backdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };
const modalShellStyle: React.CSSProperties = { position: "relative", width: "100%", maxWidth: "440px" };
const resultBoxStyle: React.CSSProperties = { padding: "12px 14px", borderRadius: "12px", border: "1px solid var(--border)", fontSize: "13px", display: "flex", flexDirection: "column", gap: "6px" };
const dayRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", gap: "8px", fontSize: "12px", color: "var(--text-secondary)" };

function formatDisplay(dateKey: string): string {
  const [y, m, d] = dateKey.split("-");
  return `${d}/${m}/${y}`;
}

export default function ReconstructLegacyTool() {
  const { token } = useAuth();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ReconstructResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = from.trim() !== "" && to.trim() !== "";

  const handleOpenConfirm = () => {
    if (!canSubmit) return;
    setResult(null);
    setError(null);
    setConfirming(true);
  };

  const handleConfirm = async () => {
    if (submitting) return; // nunca doble submit, aunque se haga doble click
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch("/v1/utils/reconstruct-legacy", {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({ from, to }),
      });
      const payload: ReconstructResponse = await res.json().catch(() => ({ ok: false, error: "Respuesta inválida del servidor" }));
      if (!res.ok && res.status !== 207) {
        throw new Error(payload?.error || "No se pudo reconstruir el rango");
      }
      setResult(payload);
      setConfirming(false);
    } catch (e: any) {
      setError(e.message || "No se pudo reconstruir el rango");
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <div style={sectionStyle}>
        <div>
          <h2 style={titleStyle}>🛠️ Reconstrucción Legacy (herramienta temporal)</h2>
          <p style={noteStyle}>
            Vuelve a consultar e importar envíos de Lightdata para un rango de fechas puntual — para usar después de un
            deploy o una limpieza de datos regenerables. Nunca borra nada: actualiza por UPSERT los envíos existentes y
            agrega los que falten. No toca Snapshot, Overrides, Reportes ni Liquidaciones.
          </p>
        </div>

        <div style={rowStyle}>
          <div style={fieldStyle}>
            <Input label="Desde" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div style={fieldStyle}>
            <Input label="Hasta" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <Button onClick={handleOpenConfirm} disabled={!canSubmit || submitting}>
            Reconstruir rango
          </Button>
        </div>

        {error && <div style={{ color: "var(--danger, #d1242f)", fontSize: "13px" }}>{error}</div>}

        {result && (
          <div style={{ ...resultBoxStyle, borderColor: result.ok ? "var(--success, #2ea043)" : "var(--danger, #d1242f)" }}>
            <strong style={{ color: result.ok ? "var(--success, #2ea043)" : "var(--danger, #d1242f)" }}>
              {result.ok ? "✅ Reconstrucción completa" : "⚠️ Reconstrucción con errores"}
            </strong>
            <span>
              Rango: {result.from ? formatDisplay(result.from) : "—"} → {result.to ? formatDisplay(result.to) : "—"}
            </span>
            {result.totals && (
              <span>
                Recibidos: {result.totals.received} · Nuevos: {result.totals.inserted} · Actualizados: {result.totals.updated} · Sin
                cambios: {result.totals.unchanged} · Salteados: {result.totals.skipped}
                {result.totals.failedDays > 0 && ` · Días con error: ${result.totals.failedDays}`}
              </span>
            )}
            {result.days && result.days.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "3px", marginTop: "4px" }}>
                {result.days.map((d) => (
                  <div key={d.date} style={dayRowStyle}>
                    <span>{formatDisplay(d.date)}</span>
                    <span>
                      {d.error ? (
                        <span style={{ color: "var(--danger, #d1242f)" }}>Error: {d.error}</span>
                      ) : (
                        `recibidos ${d.received} · nuevos ${d.inserted} · actualizados ${d.updated}`
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {confirming && (
        <div style={overlayStyle}>
          <div style={backdropStyle} onClick={() => !submitting && setConfirming(false)} />
          <div style={modalShellStyle}>
            <Card padding="20px" style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <h3 style={titleStyle}>Confirmar reconstrucción</h3>
              <p style={{ margin: 0, fontSize: "13px", color: "var(--text-primary)" }}>
                Se volverán a consultar e importar los envíos entre {formatDisplay(from)} y {formatDisplay(to)}. Los
                registros existentes podrán actualizarse con la información actual de Lightdata.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <Button variant="secondary" onClick={() => setConfirming(false)} disabled={submitting}>
                  Cancelar
                </Button>
                <Button onClick={handleConfirm} disabled={submitting}>
                  {submitting ? "Reconstruyendo…" : "Reconstruir"}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </Card>
  );
}
